import { Component, OnInit } from '@angular/core';
import { style, animate, transition, trigger } from '@angular/animations';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { LegalAd } from '../../models/LegalAd';
import _ from "lodash";

import { AuthorizationService } from 'src/services/authorization.service';
import { LoadingIndicatorService } from '../../services/loading-indicator.service';
import { LegalAdService } from '../../services/legal-ad.service';
import { DataHandlerService } from 'src/services/data-handler.service';
import { MessageService } from 'primeng/api'
import { AppInsightsService } from '../../services/app-insights.service';
import { Division } from '../../models/Division';
import { RequestorItem } from 'src/models/RequestorItem';
import { LegalAdReviewForm } from 'src/models/LegalAdReviewForm';
import { Constants } from '../Constants';
import { ClaimsEnum } from 'src/models/claims.enum';
import { OutsideLegal } from 'src/models/OutsideLegal';
import { displayDate } from '../shared/date-utils';
import { IndexedDbService } from 'src/services/indexed-db.service';
// Cache key for Outside Legals
// This key is used to store the Outside Legals in localStorage for caching purposes.
// It allows the application to retrieve the cached data without making unnecessary API calls.
// The key is defined as a constant to ensure consistency across the application.
// It is used in the HomeComponent to check if the Outside Legals are already cached before making a network request.
// If the data is cached, it will be used to populate the outsideLegalOptions and avoid redundant API calls.
// If the data is not cached, it will be fetched from the server and then stored in localStorage for future use.
// This caching mechanism improves performance by reducing the number of API calls made by the application, especially for data that does not change frequently.
// It also enhances the user experience by providing faster access to the data when navigating through the application
const OutsideLegalCacheKey = 'OutsideLegalsCacheKey';

@Component({
    selector: 'app-home',
    templateUrl: './home.component.html',
    styleUrls: ['./home.component.css'],
    animations: [
        trigger('fadeInOut', [
            transition(':enter', [
                style({ opacity: 0 }),
                animate(800, style({ opacity: 1 }))
            ]),
            transition(':leave', [
                animate(800, style({ opacity: 0 }))
            ])
        ])
    ]
})
export class HomeComponent implements OnInit {
    sub: any;
    greetingMessage: string;

    approvalsCollapse: boolean = false;
    requestsCollapse: boolean = false;
    loading: boolean = false;
    loadingDivisions: boolean = false;

    forms: any;
    cachedForms: any;

    divisionData: Division[];
    selectedDivisionId: number;

    requestorItems: RequestorItem[] = [];
    selectedRequestors: string[] = [];
    selectedFilterDate: Date;

    currentRetryCount: number = 0;
    maxRetries: number = 20; // 20 * 500ms = 10 seconds

    // Roles
    corpsys = false;
    admin = false;
    requestor = false;
    approver = false;

    // Add detected browser timezone
    userTimeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

    public outsideLegalOptions: OutsideLegal[] = [];
    public selectedOutsideLegals: OutsideLegal[] = [];
    private financialAreas: any[] = [];

    constructor(
        private authService: AuthorizationService,
        private appInsightsService: AppInsightsService,
        private legalAdService: LegalAdService,
        private dataHandlerService: DataHandlerService,
        private loadingService: LoadingIndicatorService,
        private messageService: MessageService,
        private router: Router,
        private indexedDbService: IndexedDbService,
    ) {
        this.appInsightsService.logPageView('home.component', '/home');
        this.sub = loadingService.onLoadingChanged.subscribe(isLoading => this.loading = isLoading);
    }

    ngOnInit() {
        this.currentRetryCount = 0;
        this.formatGreetingMessage();
        if (this.authService.authenticated() != true && this.currentRetryCount < this.maxRetries) {
            this.currentRetryCount++;
            setTimeout(() => {
                this.ngOnInit();
            }, 500);
        }

        this.getFormData();

        let role = this.authService.getRoles();

        switch (role[0]) {
            case ClaimsEnum.LegalAdApprove:
                this.approver = true;
                break;
            case ClaimsEnum.LegalAdAdmin:
                this.admin = true;
                break;
            case ClaimsEnum.LegalAdCorpSystems:
                this.corpsys = true;
                break;
            case ClaimsEnum.LegalAdRequest:
                this.requestor = true;
                break;
            default:
                break;
        }
        this.currentRetryCount = 0;
    }

    ngOnDestroy() {
        if (this.sub)
            this.sub.unsubscribe();
    }

    get authenticated(): boolean {
        return this.authService.authenticated();
    }

    // --- Alpha order below this line ---



    getDivisionData(value) {
        this.selectedDivisionId = -1;

        this.sub = this.legalAdService.getFinancialDivisions(value).subscribe((divisions: any) => {
            if (!!divisions && divisions.length > 0) {
                // Manually add "Home Office" as the first entry
                const homeOfficeDivision: Division = this.legalAdService.getHomeOfficeDivision();
                this.divisionData = [homeOfficeDivision, ...divisions];
            }
            else {
                // If no divisions, still add "Home Office"
                this.divisionData = [this.legalAdService.getHomeOfficeDivision()];
            }
        }, error => {
            console.log(error.message);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: "There was an error retrieving the divisions..." });
        });
    }

    getFinancialAreas(){
        this.sub = this.legalAdService.getFinancialAreas().subscribe((areas: any) => {
            if (!!areas && areas.length > 0) {
                this.financialAreas = areas;
            }
        }, error => {
            console.log(error.message);
            this.messageService.add({ severity: 'error', summary: 'Error', detail: "There was an error retrieving the financial areas..." });
        });
    }

    getFormData() {
        this.getForms();
        this.getDivisionData(null);
    }

    getForms() {
        this.loading = true;
        this.forms = null;
        this.selectedDivisionId = -1;
        this.messageService.add({ severity: 'success', summary: 'Success', detail: "Retrieving Legal Ad Forms..." });

        this.sub = this.legalAdService.checkFormsModified().subscribe((dates: any) => {
            if (!!dates && dates.length != 0) {
                const count = dates['@odata.count'];
                const cacheCount = localStorage.getItem(Constants.FormsCountCacheKey)
                const latestModifiedDate = new Date((new Date(dates[0].modifiedDate)).setMilliseconds(0)); //clear milliseconds difference
                const cacheModifiedDate = new Date((new Date(localStorage.getItem(Constants.ModifiedDateCacheKey)).setMilliseconds(0))); //clear milliseconds difference
                if (!cacheCount || count != cacheCount || !cacheModifiedDate || latestModifiedDate > cacheModifiedDate) {
                    // Use forkJoin to fetch forms and outside legals together
                    this.sub = forkJoin({
                        forms: this.legalAdService.getForms(),
                        outsideLegals: this.legalAdService.getOutsideLegals()
                    }).subscribe(({ forms, outsideLegals }) => {
                        // Set Forms
                        this.forms = forms.sort((a, b) => {
                            // 1. isTimeSensitive (true first)
                            if (a.isTimeSensitive !== b.isTimeSensitive) {
                                return a.isTimeSensitive ? -1 : 1;
                            }
                            // 2. dateSubmitted (descending)
                            const dateDiff = this.getTime(b.dateSubmitted) - this.getTime(a.dateSubmitted);
                            if (dateDiff !== 0) return dateDiff;
                            // 3. legalAdId (descending)
                            return b.legalAdId - a.legalAdId;
                        });
                        this.forms = forms.sort((a, b) => this.getTime(b.dateSubmitted) - this.getTime(a.dateSubmitted))
                            .sort((a, b) => b.legalAdId - a.legalAdId);
                        this.cachedForms = this.forms;
                        // Set Cache for Forms, Count and ModifiedDate

                        this.cacheFormsData();
                        localStorage.setItem(Constants.FormsCountCacheKey, count);
                        localStorage.setItem(Constants.ModifiedDateCacheKey, latestModifiedDate ? latestModifiedDate.toUTCString() : null);
                        // Cache OutsideLegals
                        localStorage.setItem(OutsideLegalCacheKey, JSON.stringify(outsideLegals));
                        // Set Requestors
                        this.requestorItems = this.getRequestorsFromForms(this.forms);
                        // Set OutsideLegal options
                        this.outsideLegalOptions = outsideLegals;
                        // Populate each LegalAd's outsideLegals property
                        this.populateOutsideLegalsForAdsFromSelf(this.forms, this.outsideLegalOptions);
                    }, error => {
                        console.log(error.message);
                        // Rollback to cached forms and outsideLegals if available
                        const cachedForms = localStorage.getItem(Constants.FormsCacheKey);
                        const cachedOutsideLegals = localStorage.getItem(OutsideLegalCacheKey);
                        if (cachedForms) {
                            this.forms = JSON.parse(cachedForms);
                            this.cachedForms = this.forms;
                            this.requestorItems = this.getRequestorsFromForms(this.forms);
                            if (cachedOutsideLegals) {
                                this.outsideLegalOptions = JSON.parse(cachedOutsideLegals);
                            }
                            this.populateOutsideLegalsForAdsFromSelf(this.forms, this.outsideLegalOptions || []);
                        }
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: "There was an error retrieving your Legal Ad Forms or Outside Legals. Showing cached data if available." });
                    });
                }
                else {
                    // Set Forms from cache
                    this.forms = JSON.parse(localStorage.getItem(Constants.FormsCacheKey));
                    this.cachedForms = this.forms;
                    // Set Requestors
                    this.requestorItems = this.getRequestorsFromForms(this.forms);
                    // Optionally, fetch outsideLegals if needed for cached forms
                    this.legalAdService.getOutsideLegals().subscribe((outsideLegals: OutsideLegal[]) => {
                        this.outsideLegalOptions = outsideLegals;
                        localStorage.setItem(OutsideLegalCacheKey, JSON.stringify(outsideLegals));
                        this.populateOutsideLegalsForAdsFromSelf(this.forms, this.outsideLegalOptions);
                    }, error => {
                        // Rollback to cached outsideLegals if available
                        const cachedOutsideLegals = localStorage.getItem(OutsideLegalCacheKey);
                        if (cachedOutsideLegals) {
                            this.outsideLegalOptions = JSON.parse(cachedOutsideLegals);
                            this.populateOutsideLegalsForAdsFromSelf(this.forms, this.outsideLegalOptions);
                        }
                        this.messageService.add({ severity: 'error', summary: 'Error', detail: "There was an error retrieving Outside Legals. Showing cached data if available." });
                    });
                }
            }
        }, error => {
            console.log(error.message);
            // Rollback to cached forms and outsideLegals if available
            const cachedForms = localStorage.getItem(Constants.FormsCacheKey);
            const cachedOutsideLegals = localStorage.getItem(OutsideLegalCacheKey);
            if (cachedForms) {
                this.forms = JSON.parse(cachedForms);
                this.cachedForms = this.forms;
                this.requestorItems = this.getRequestorsFromForms(this.forms);
                if (cachedOutsideLegals) {
                    this.outsideLegalOptions = JSON.parse(cachedOutsideLegals);
                }
                this.populateOutsideLegalsForAdsFromSelf(this.forms, this.outsideLegalOptions || []);
            }
            this.messageService.add({ severity: 'error', summary: 'Error', detail: "There was an error retrieving your Legal Ad Forms. Showing cached data if available." });
        });
    }

    async cacheFormsData() {
        try {
            await this.indexedDbService.setItem('forms', 'all', this.forms);
        } catch (error) {
            console.error("Error setting IndexedDB items:", error);
        }
    }

    async getFormsFromCache() {
        try {
            const cachedForms = await this.indexedDbService.getItem('forms', 'all');
            if (cachedForms) {
                this.forms = cachedForms;
            }
        } catch (error) {
            console.error("Error getting IndexedDB items:", error);
        }
    }

    cacheOutsideLegalsData() {
        try {
            let outsideLegalsToCache = this.outsideLegalOptions;
            let maxToCache = 200;
            try {
                if (Array.isArray(this.outsideLegalOptions) && this.outsideLegalOptions.length > maxToCache) {
                    outsideLegalsToCache = this.outsideLegalOptions.slice(0, maxToCache);
                }
                localStorage.setItem(OutsideLegalCacheKey, JSON.stringify(outsideLegalsToCache));
            } catch (error) {
                // If quota exceeded, try reducing to 100
                if (Array.isArray(this.outsideLegalOptions) && this.outsideLegalOptions.length > 100) {
                    outsideLegalsToCache = this.outsideLegalOptions.slice(0, 100);
                }
                try {
                    localStorage.setItem(OutsideLegalCacheKey, JSON.stringify(outsideLegalsToCache));
                } catch (err) {
                    console.error("Error setting localStorage items even after reducing to 100:", err);
                }
            }
        } catch (error) {
            console.error("Error setting localStorage items:", error);
        }
    }

    getOutsideLegalNames(outsideLegals: OutsideLegal[]): string {
        return outsideLegals?.map(o => o.fullName).join(', ') || '';
    }

    getRequestorsFromForms(forms: LegalAd[]): RequestorItem[] {
        var hash = Object.create(null);
        var requestors = [];
        for (var i = 0; i < this.forms.length; i++) {
            if (!hash[this.forms[i].requestorUserId]) {
                hash[this.forms[i].requestorUserId] = true;
                var requestor = { label: this.forms[i].requestor, value: this.forms[i].requestorUserId };
                requestors.push(requestor);
            }
        }
        return requestors.sort((a, b) => a.label.localeCompare(b.label));
    }

    getTime(date?: Date) {
        return date != null ? new Date(date).getTime() : 0;
    }

    populateOutsideLegalsForAdsFromSelf(legalAds: any[], allOutsideLegals: OutsideLegal[]) {
        legalAds.forEach(ad => {
            if (Array.isArray(ad?.legalAdOutsideLegals) && ad?.legalAdOutsideLegals.length > 0) {
                // Find the last entry in legalAdOutsideLegals
                const lastJoin = ad.legalAdOutsideLegals[ad.legalAdOutsideLegals.length - 1];
                // Find the matching OutsideLegal object
                const lastOutsideLegal = allOutsideLegals.find(ol => ol.outsideLegalId === lastJoin.outsideLegalId);
                ad.outsideLegals = lastOutsideLegal ? [lastOutsideLegal] : [];
            } else {
                ad.outsideLegals = [];
            }
        });
    }

    saveLegalAd() {
        const legalAd = new LegalAd();
        // ...set other properties...
        legalAd.outsideLegals = this.selectedOutsideLegals;
        this.legalAdService.createLegalAdForm(legalAd);
    }

    scrollTo(className: string): void {
        const elementList = document.querySelectorAll('.' + className);
        const element = elementList[0] as HTMLElement;
        element.scrollIntoView({ behavior: 'smooth' });
    }

    sendSelectedFormToRoute(selectedForm: LegalAd): void {
        this.router.navigate(['/form', selectedForm.legalAdId]);
    }

    setApprovalsCollapse() {
        this.approvalsCollapse = !this.approvalsCollapse;
    }

    setFilteredFormsList() {
        console.log('setFilteredFormsList')
        if (this.cachedForms && this.cachedForms.length > 0) {
            var filteredList = this.cachedForms;

            // Filter by Date, if one was selected.
            if (this.selectedFilterDate) {
                var filterDate = this.selectedFilterDate;
                filteredList = filteredList.filter(function (form) { return form.dateSubmitted && new Date(form.dateSubmitted).setHours(0, 0, 0, 0) == new Date(filterDate).setHours(0, 0, 0, 0) });
            }

            // Filter by Division, if one was selected.
            const selectedDivisionId = this.selectedDivisionId;
            if (this.selectedDivisionId >= 0){
                const homeOffice = this.legalAdService.getHomeOfficeDivision();
                if (this.selectedDivisionId === homeOffice.id) {
                    // if Home Office selected, search for marketId = 9999 or areaId = Home Area
                    const homeArea = this.financialAreas.find(a => a.name === 'Home Office');
                    filteredList = filteredList.filter(function (form) { return form.marketId === homeOffice.id || form.areaId === homeArea?.id });
                } else {
                    filteredList = filteredList.filter(function (form) { return form.marketId === selectedDivisionId });
                }
            }

            // Filter by Requestor, if one (or more) was selected.
            if (this.selectedRequestors && this.selectedRequestors.length > 0) {
                var requestorFilteredList = []
                this.selectedRequestors.forEach(requestor => {
                    requestorFilteredList = _.union(requestorFilteredList, filteredList.filter(function (form) { return form.requestorUserId === requestor }));
                });
                filteredList = requestorFilteredList;
            }

            this.forms = filteredList;
        }
    }

    undoFilters() {
        this.selectedDivisionId = -1;
        this.selectedFilterDate = null;
        this.selectedRequestors = [];
        this.forms = this.cachedForms;
    }

    setRequestsCollapse() {
        this.requestsCollapse = !this.requestsCollapse;
    }

    async exportExcel() {
        if (this.forms && this.forms.length > 0) {
            this.sub = this.dataHandlerService.getAllDataForExport().subscribe((data: any) => {
                this.dataHandlerService.exportDataToExcel(this.forms as LegalAdReviewForm[], data);
            }, error => {
                console.log(error.message);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: "There was an getting the Legal Ad Support Data..." });
            });
        }
    }

    formatGreetingMessage() {
        var greet = '';
        var name = '';

        var myDate = new Date();
        var hrs = myDate.getHours();

        if (hrs < 12)
            greet = 'Good Morning';
        else if (hrs >= 12 && hrs <= 17)
            greet = 'Good Afternoon';
        else if (hrs >= 17 && hrs <= 24)
            greet = 'Good Evening';

        if (this.authService.authenticated()) {
            name = this.authService.getName();
        }
        this.greetingMessage = `${greet} ${name}`.trim() + '!';

    }

    getTzDate(date: string | Date): string {
        if (!date) {
            return '';
        }
        return displayDate(date, this.userTimeZone);
    }
}
